import { Component, ChangeDetectorRef } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-proyects-set-data',
  templateUrl: './proyects-set-data.component.html',
  styleUrls: ['./proyects-set-data.component.css']
})
export class ProyectsSetDataComponent {
  proyectsToChange: any[] = [];
  filteredProjects: any[] = [];
  proyectData: any | null = null;
  isLoading: boolean = true;

  // Filtros
  selectedBlockchainFilter: string = 'all';
  selectedStatusFilter: string = 'all';
  selectedSaleTypeFilter: string = 'all';

  constructor(
    private afs: AngularFirestore,
    private cdr: ChangeDetectorRef,
    private storage: AngularFireStorage
  ) {
    // Mostrar todos los proyectos, no solo los que no están en blockchain
    // Usamos snapshotChanges para obtener también los IDs de los documentos
    this.isLoading = true;
    this.afs.collection('investments').snapshotChanges().subscribe({
      next: (changes: any) => {
        if (changes !== undefined && changes !== null && Array.isArray(changes)) {
          // Mapear los cambios para incluir el ID del documento
          this.proyectsToChange = changes.map((change: any) => {
            const data = change.payload.doc.data();
            const id = change.payload.doc.id;
            return { ...data, id: id };
          });
          
          // Aplicar filtros después de cargar los datos
          this.applyFilters();
        } else {
          this.proyectsToChange = [];
          this.filteredProjects = [];
        }
        
        // Marcar como cargado
        this.isLoading = false;
        
        // Forzar detección de cambios
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('❌ [ProyectsSetData] Error al cargar proyectos:', error);
        this.proyectsToChange = [];
        this.filteredProjects = [];
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  editProyect(proyect: any) {
    this.proyectData = proyect;
  }

  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
      
      // Mostrar placeholder si existe
      const container = img.parentElement;
      if (container) {
        const placeholder = container.querySelector('.image-error-placeholder');
        if (placeholder) {
          (placeholder as HTMLElement).style.display = 'flex';
        }
      }
    }
  }

  deselectProyect() {
    this.proyectData = null;
  }

  // Método para trackBy en ngFor (mejora el rendimiento y ayuda con la detección de cambios)
  trackByProjectId(index: number, proyect: any): any {
    return proyect?.id || index;
  }

  // Método para verificar si un proyecto está en blockchain
  isProjectInBlockchain(proyect: any): boolean {
    return proyect.blockchain === true;
  }

  // Método para obtener el estado del proyecto
  getProjectStatus(proyect: any): string {
    if (proyect.blockchain === true) {
      return 'Desplegado en Blockchain';
    }
    return 'No desplegado';
  }

  // Método para aplicar filtros
  applyFilters(): void {
    this.filteredProjects = this.proyectsToChange.filter(proyect => {
      // Filtro por estado de blockchain
      // Tolerante a undefined/null: si blockchain no está definido, se considera "no-blockchain"
      const blockchainMatch = this.selectedBlockchainFilter === 'all' ||
        (this.selectedBlockchainFilter === 'blockchain' && proyect.blockchain === true) ||
        (this.selectedBlockchainFilter === 'no-blockchain' && (proyect.blockchain !== true || proyect.blockchain === undefined || proyect.blockchain === null));

      // Filtro por estado del proyecto
      // Tolerante a undefined/null: si no tiene estado, pasa el filtro cuando es 'all'
      const projectStatus = proyect.proyectStatus || proyect.status;
      const statusMatch = this.selectedStatusFilter === 'all' ||
        projectStatus === this.selectedStatusFilter;

      // Filtro por tipo de venta
      // Tolerante a undefined/null: si onlyInversors no está definido, se considera "public"
      const saleTypeMatch = this.selectedSaleTypeFilter === 'all' ||
        (this.selectedSaleTypeFilter === 'public' && (!proyect.onlyInversors || proyect.onlyInversors === false)) ||
        (this.selectedSaleTypeFilter === 'private' && proyect.onlyInversors === true);

      const passes = blockchainMatch && statusMatch && saleTypeMatch;

      return passes;
    });

    // Forzar detección de cambios después de aplicar filtros
    this.cdr.detectChanges();
  }

  // Método para limpiar filtros
  clearFilters(): void {
    this.selectedBlockchainFilter = 'all';
    this.selectedStatusFilter = 'all';
    this.selectedSaleTypeFilter = 'all';
    this.applyFilters();
  }

  // Método para eliminar proyecto y sus imágenes
  async deleteProject(proyect: any) {
    // Paso 1: Primera confirmación
    const firstResult = await Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción eliminará el proyecto y todas sus imágenes. Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar'
    });

    if (!firstResult.isConfirmed) {
      return;
    }

    // Paso 2: Segunda confirmación (varía si está en blockchain o no)
    let secondResult;

    if (this.isProjectInBlockchain(proyect)) {
      secondResult = await Swal.fire({
        title: '⚠️ ADVERTENCIA CRÍTICA',
        html: `Este proyecto está <b>DESPLEGADO EN BLOCKCHAIN</b>.<br><br>
               Eliminarlo de la web <b>NO LO ELIMINARÁ</b> de la Blockchain, pero desaparecerá de tu panel de administración.<br><br>
               Para confirmar, escribe <b>ELIMINAR</b> en el campo de abajo:`,
        input: 'text',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Entiendo, ELIMINAR PROYECTO',
        cancelButtonText: 'Cancelar',
        preConfirm: (value) => {
          if (value !== 'ELIMINAR') {
            Swal.showValidationMessage('Debes escribir "ELIMINAR" exactamente para confirmar.');
          }
        }
      });
    } else {
      secondResult = await Swal.fire({
        title: 'Confirmación final',
        text: "¿Realmente quieres eliminar este proyecto definitivamente?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar definitivamente',
        cancelButtonText: 'Cancelar'
      });
    }

    if (!secondResult.isConfirmed) {
      return;
    }

    try {
      // Mostrar loading
      Swal.fire({
        title: 'Eliminando...',
        text: 'Por favor espera mientras se elimina el proyecto y sus archivos',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Eliminar imágenes del storage
      await this.deleteProjectImages(proyect);

      // Eliminar el documento de Firestore
      await this.afs.collection('investments').doc(proyect.id).delete();

      Swal.fire(
        'Eliminado',
        'El proyecto y sus archivos han sido eliminados.',
        'success'
      ).then(() => {
        // Forzar actualización de la lista
        this.cdr.detectChanges();
      });
    } catch (error) {
      console.error('Error eliminando proyecto:', error);
      Swal.fire(
        'Error',
        'No se pudo eliminar el proyecto completamente. Algunos archivos pueden quedar en el storage.',
        'error'
      );
    }
  }

  // Método para eliminar todas las imágenes del proyecto
  private async deleteProjectImages(proyect: any): Promise<void> {
    const projectId = proyect.id;
    const imagesToDelete: Promise<void>[] = [];

    // Eliminar imagen principal
    if (proyect.mainImage) {
      try {
        const mainImageRef = this.storage.refFromURL(proyect.mainImage);
        imagesToDelete.push(
          mainImageRef.delete().toPromise().catch(err => {
            console.warn('Error eliminando imagen principal:', err);
          })
        );
      } catch (error) {
        console.warn('Error obteniendo referencia de imagen principal:', error);
      }
    }

    // Eliminar imágenes adicionales
    if (proyect.images && Array.isArray(proyect.images)) {
      proyect.images.forEach((imageUrl: string) => {
        if (imageUrl) {
          try {
            const imageRef = this.storage.refFromURL(imageUrl);
            imagesToDelete.push(
              imageRef.delete().toPromise().catch(err => {
                console.warn('Error eliminando imagen:', err);
              })
            );
          } catch (error) {
            console.warn('Error obteniendo referencia de imagen:', error);
          }
        }
      });
    }

    // Eliminar documentos PDF
    if (proyect.urlWhitepapperPdf) {
      try {
        const whitepaperRef = this.storage.refFromURL(proyect.urlWhitepapperPdf);
        imagesToDelete.push(
          whitepaperRef.delete().toPromise().catch(err => {
            console.warn('Error eliminando whitepaper:', err);
          })
        );
      } catch (error) {
        console.warn('Error obteniendo referencia de whitepaper:', error);
      }
    }

    if (proyect.urlDosierPdf) {
      try {
        const dosierRef = this.storage.refFromURL(proyect.urlDosierPdf);
        imagesToDelete.push(
          dosierRef.delete().toPromise().catch(err => {
            console.warn('Error eliminando dosier:', err);
          })
        );
      } catch (error) {
        console.warn('Error obteniendo referencia de dosier:', error);
      }
    }

    if (proyect.urlInfoTokenPdf) {
      try {
        const infoTokenRef = this.storage.refFromURL(proyect.urlInfoTokenPdf);
        imagesToDelete.push(
          infoTokenRef.delete().toPromise().catch(err => {
            console.warn('Error eliminando info token:', err);
          })
        );
      } catch (error) {
        console.warn('Error obteniendo referencia de info token:', error);
      }
    }

    // Esperar a que se eliminen todos los archivos
    await Promise.all(imagesToDelete);
  }
}
